import React, { useEffect, useState, useCallback, useRef } from "react";
import {
    Box,
    Typography,
    IconButton,
    Chip,
    useTheme,
    useMediaQuery,
} from "@mui/material";

import FilterPanelContainer from "../Filter/Filterpopup";

// assets
import drillDownArrow from "../../assets/images/selectedicons/DrillDownArrow.svg";
import datadrillhover from "../../assets/images/selectedicons/Data Drill Down1.svg";
import datadrilldown from "../../assets/images/selectedicons/DataDrillDown.svg";
import { useFilterContext } from "../../../src/context/FilterContext";
import { fetchMyDashboard } from "../../services/mydashboard.service";

const DashboardHeader = ({ title,  isfiltericon, isfilter }) => {
    /* -------------------- MUI -------------------- */
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    /* -------------------- Header State -------------------- */
    const [openFilters, setOpenFilters] = useState(false);
    const [hover, setHover] = useState(false);

    /* -------------------- Filter State -------------------- */
    const [activeFilter, setActiveFilter] = useState("All");
    const [activeIdx, setActiveIdx] = useState(0);
    const [filterLoading, setFilterLoading] = useState(false);
    
    const {
        filterOptions,
        filterChips,
        activeFilters,
        setActiveFilters
    } = useFilterContext();

    /* -------------------- Filter Presets -------------------- */
    const getFilterPreset = useCallback((filterName) => {
        if (!filterOptions) return null;

        // Helper to extract names from filter options
        const getNames = (key) => (filterOptions[key] || []).map(v => v?.name || v);
        
        // Get all available options dynamically
        const allCompanies = getNames("Company");
        const allCategories = getNames("Category");
        const allBrands = getNames("Brand");
        const allSubBrands = getNames("Sub-Brand");
        const allRoutes = getNames("Route to Market");
        const allChannels = getNames("Channel");

        // Helper to find items by name
        const findByName = (key, name) => {
            const items = filterOptions[key] || [];
            return items.find(item => (item?.name || item) === name);
        };

        // Helper to get Nutrabolt brands/sub-brands
        const getNutraboltBrands = () => {
            const nutraboltBrands = allBrands.filter(brand => {
                const brandName = typeof brand === 'string' ? brand : brand?.name;
                return brandName && !brandName.toLowerCase().includes('bloom');
            });
            return nutraboltBrands.map(b => typeof b === 'string' ? b : b?.name);
        };

        const getNutraboltSubBrands = () => {
            const nutraboltSubBrands = allSubBrands.filter(subBrand => {
                const subBrandName = typeof subBrand === 'string' ? subBrand : subBrand?.name;
                return subBrandName && !subBrandName.toLowerCase().includes('bloom');
            });
            return nutraboltSubBrands.map(sb => typeof sb === 'string' ? sb : sb?.name);
        };

        // Helper to get Bloom brands/sub-brands
        const getBloomBrands = () => {
            const bloomBrands = allBrands.filter(brand => {
                const brandName = typeof brand === 'string' ? brand : brand?.name;
                return brandName && brandName.toLowerCase().includes('bloom');
            });
            return bloomBrands.map(b => typeof b === 'string' ? b : b?.name);
        };

        const getBloomSubBrands = () => {
            const bloomSubBrands = allSubBrands.filter(subBrand => {
                const subBrandName = typeof subBrand === 'string' ? subBrand : subBrand?.name;
                return subBrandName && subBrandName.toLowerCase().includes('bloom');
            });
            return bloomSubBrands.map(sb => typeof sb === 'string' ? sb : sb?.name);
        };

        // Helper to get Functional Beverage sub-brands (beverage products)
        const getFunctionalBeverageSubBrands = () => {
            const beverageSubBrands = allSubBrands.filter(subBrand => {
                const subBrandName = typeof subBrand === 'string' ? subBrand : subBrand?.name;
                // Exclude powder/supplement sub-brands
                return subBrandName && 
                    !subBrandName.toLowerCase().includes('powder') &&
                    !subBrandName.toLowerCase().includes('cellucor') &&
                    subBrandName !== 'XTEND' &&
                    subBrandName !== 'C4';
            });
            return beverageSubBrands.map(sb => typeof sb === 'string' ? sb : sb?.name);
        };

        // Helper to get Active Nutrition sub-brands (supplements/powders)
        const getActiveNutritionSubBrands = () => {
            const nutritionSubBrands = allSubBrands.filter(subBrand => {
                const subBrandName = typeof subBrand === 'string' ? subBrand : subBrand?.name;
                // Include powder and supplement sub-brands
                return subBrandName && (
                    subBrandName.toLowerCase().includes('powder') ||
                    subBrandName === 'XTEND' ||
                    subBrandName === 'C4' ||
                    subBrandName.toLowerCase().includes('cellucor')
                );
            });
            return nutritionSubBrands.map(sb => typeof sb === 'string' ? sb : sb?.name);
        };

        switch (filterName) {
            case "All":
                return {
                    Company: allCompanies,
                    Category: allCategories,
                    Brand: allBrands,
                    "Sub-Brand": allSubBrands,
                    "Route to Market": allRoutes,
                    Channel: allChannels,
                };

            case "Nutrabolt":
                return {
                    Company: ["Nutrabolt"],
                    Category: allCategories,
                    Brand: getNutraboltBrands(),
                    "Sub-Brand": getNutraboltSubBrands(),
                    "Route to Market": allRoutes,
                    Channel: allChannels,
                };

            case "Bloom":
                const bloomCategory = allCategories.find(cat => {
                    const catName = typeof cat === 'string' ? cat : cat?.name;
                    return catName && catName.toLowerCase().includes('functional');
                });
                return {
                    Company: ["Bloom"],
                    Category: bloomCategory ? [typeof bloomCategory === 'string' ? bloomCategory : bloomCategory?.name] : [],
                    Brand: getBloomBrands(),
                    "Sub-Brand": getBloomSubBrands(),
                    "Route to Market": allRoutes,
                    Channel: allChannels,
                };

            case "Functional Beverage":
                const functionalBeverageCategory = allCategories.find(cat => {
                    const catName = typeof cat === 'string' ? cat : cat?.name;
                    return catName && catName.toLowerCase().includes('functional');
                });
                const functionalBeverageBrands = allBrands.filter(brand => {
                    const brandName = typeof brand === 'string' ? brand : brand?.name;
                    return brandName && (brandName === 'C4' || brandName.toLowerCase().includes('bloom'));
                }).map(b => typeof b === 'string' ? b : b?.name);
                return {
                    Company: allCompanies,
                    Category: functionalBeverageCategory ? [typeof functionalBeverageCategory === 'string' ? functionalBeverageCategory : functionalBeverageCategory?.name] : [],
                    Brand: functionalBeverageBrands,
                    "Sub-Brand": getFunctionalBeverageSubBrands(),
                    "Route to Market": allRoutes,
                    Channel: allChannels,
                };

            case "Active Nutrition":
                const activeNutritionCategory = allCategories.find(cat => {
                    const catName = typeof cat === 'string' ? cat : cat?.name;
                    return catName && catName.toLowerCase().includes('active');
                });
                const activeNutritionBrands = getNutraboltBrands();
                return {
                    Company: ["Nutrabolt"],
                    Category: activeNutritionCategory ? [typeof activeNutritionCategory === 'string' ? activeNutritionCategory : activeNutritionCategory?.name] : [],
                    Brand: activeNutritionBrands,
                    "Sub-Brand": getActiveNutritionSubBrands(),
                    "Route to Market": allRoutes,
                    Channel: allChannels,
                };

            default:
                return null;
        }
    }, [filterOptions]);

    const [filterPresets, setFilterPresets] = useState(null);
    const isInitializedRef = useRef(false);
    
    // Initialize filter presets when filterOptions are available
    useEffect(() => {
        if (filterOptions && Object.keys(filterOptions).length > 0 && !isInitializedRef.current) {
            const allPreset = getFilterPreset("All");
            if (allPreset) {
                // Set all states synchronously to ensure proper initialization
                isInitializedRef.current = true; // Set ref first to prevent race condition
                setFilterPresets(allPreset);
                setActiveFilters(allPreset);
                setActiveFilter("All"); // Explicitly set activeFilter to "All" on initial load
            }
        }
    }, [filterOptions, getFilterPreset, setActiveFilters]);

    useEffect(() => {
        if (!filterOptions || !activeFilters || !isInitializedRef.current) return;

        // Check if activeFilters are empty (not initialized yet) - skip check
        const hasAnySelection = Object.values(activeFilters).some(arr => (arr || []).length > 0);
        if (!hasAnySelection) {
            // If no selections, ensure "All" is set (initialization should handle this, but as fallback)
            setActiveFilter("All");
            return;
        }

        // Check if current filters match any preset
        const presetNames = [
            "All",
            "Nutrabolt",
            "Bloom",
            "Functional Beverage",
            "Active Nutrition",
        ];

        let matchedPreset = null;

        // First check if "All" is selected
        const isAllSelected = Object.keys(filterOptions).every(key => {
            const allValues = (filterOptions[key] || []).map(v => v.name || v);
            const selected = activeFilters[key] || [];
            return selected.length === allValues.length && allValues.length > 0;
        });

        if (isAllSelected) {
            setActiveFilter("All");
            return;
        }

        // Check other presets
        for (const presetName of presetNames) {
            if (presetName === "All") continue; // Already checked above
            
            const preset = getFilterPreset(presetName);
            if (!preset) continue;

            const isMatching = Object.keys(preset).every((key) => {
                // Normalize values to strings for comparison
                const presetValues = [...(preset[key] || [])]
                    .map(v => typeof v === 'string' ? v : v?.name || v)
                    .sort();
                const currentValues = [...(activeFilters[key] || [])]
                    .map(v => typeof v === 'string' ? v : v?.name || v)
                    .sort();

                return (
                    presetValues.length === currentValues.length &&
                    presetValues.every((val, idx) => val === currentValues[idx])
                );
            });

            if (isMatching) {
                matchedPreset = presetName;
                break;
            }
        }

        // Set the matched preset or Custom
        if (matchedPreset) {
            setActiveFilter(matchedPreset);
        } else {
            setActiveFilter("Custom");
        }

    }, [activeFilters, filterOptions, getFilterPreset]);
    /* -------------------- Handlers -------------------- */
    const handleToggleFilters = () => {
        setOpenFilters((prev) => !prev);
    };

   const handleFilterClick = (filter) => {
        if (!filterOptions) return;

        setFilterLoading(true);

        /* ----- CUSTOM ----- */
        if (filter === "Custom") {
            setOpenFilters(true);
            setFilterLoading(false);
            return;
        }

        // Use getFilterPreset to get the preset dynamically
        const preset = getFilterPreset(filter);
        
        if (preset) {
            setTimeout(() => {
                setActiveFilters(preset);
                setActiveFilter(filter);
                setFilterPresets(preset);
                setFilterLoading(false);
            }, 800);
        } else {
            setFilterLoading(false);
        }
    };
      const buildGqlFilters = (filters) => {

        const extract = (items) =>
            (items && items.length) ? { in: items } : null;

        const gqlFiltersRaw = {
            companyName: extract(filters.Company),
            categoryName: extract(filters.Category),
            brandName: extract(filters.Brand),
            subBrandSubcategory: extract(filters["Sub-Brand"]),
            routeToMarket: extract(filters["Route to Market"]),
            channel: extract(filters.Channel)
        };
        // Remove null/undefined values to avoid backend errors
        return Object.fromEntries(
            Object.entries(gqlFiltersRaw).filter(([_, value]) => value !== null && value !== undefined)
        );
    };

    useEffect(() => {

        if (!activeFilters) return;

        const hasSelection = Object.values(activeFilters)
            .some(arr => (arr || []).length);

        if (!hasSelection) return;

        const gqlFilters = buildGqlFilters(activeFilters);

        fetchMyDashboard("YTD", "YTD", gqlFilters)
            .catch(console.error);

    }, [activeFilters]);

    

    const handleFiltersChange = useCallback(
        (newFilters) => {
            if (activeFilter === "Custom") return;

            const presetNames = [
                "All",
                "Nutrabolt",
                "Bloom",
                "Functional Beverage",
                "Active Nutrition",
            ];

            let matchedPreset = null;

            for (const presetName of presetNames) {
                const preset = getFilterPreset(presetName);
                if (!preset) continue;

                const isMatching = Object.keys(preset).every((key) => {
                    const presetValues = [...(preset[key] || [])].sort();
                    const currentValues = [...(newFilters[key] || [])].sort();

                    return (
                        presetValues.length === currentValues.length &&
                        presetValues.every((val, idx) => val === currentValues[idx])
                    );
                });

                if (isMatching) {
                    matchedPreset = presetName;
                    break;
                }
            }

            if (!matchedPreset && activeFilter !== "Custom") {
                setActiveFilter("Custom");
                setFilterPresets(null);
            }
        },
        [activeFilter, getFilterPreset]
    );

    /* ===================================================== */
    return (

        <Box
            sx={{
                position: "sticky",
                top: 0,
                zIndex: 1200,
            }}
        >
            {/* ================= HEADER ================= */}
            <Box
                className="top-bar-custom"
                sx={{
                    display: "flex",
                    flexDirection: isMobile ? "column" : "row",
                    alignItems: isMobile ? "flex-start" : "center",
                    justifyContent: isMobile ? "center" : "space-between",
                    // mb: "8px",
                    gap: isMobile ? 1 : 0,
                    background: '#FFFFFF !important',
                    padding: title === 'Intelligence' ? "0px 40px 0px 20px !important" : "0px 40px 0px 40px !important",
                    height: title === 'Intelligence' ? "68px" : isMobile ? "96px" : "68px"
                }}
            >
                {/* Title + Toggle */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography
                        sx={{
                            fontFamily: "Inter",
                            fontSize: "32px",
                            lineHeight: title === "Intelligence" ? "38px" : "19px",
                            fontWeight: 200,
                            color: "#111111",
                        }}
                    >
                        {title}
                    </Typography>
                    {isfiltericon && <IconButton
                        onClick={handleToggleFilters}
                        onMouseEnter={() => setHover(true)}
                        onMouseLeave={() => setHover(false)}
                        sx={{
                            width: 36,
                            height: 36,
                            p: 0,
                            "&:hover": { background: "transparent", boxShadow: 'none', },
                        }}
                    >
                        <img
                            src={
                                openFilters
                                    ? drillDownArrow
                                    : hover
                                        ? datadrillhover
                                        : datadrilldown
                            }
                            alt="Toggle"
                            width="36"
                            height="36"
                        />
                    </IconButton>}

                </Box>

                {/* Category Chips */}
                {isfilter && <Box
                    sx={{
                        display: "flex",
                        gap: 1,
                        width: isMobile ? "100%" : "auto",
                        overflowX: isMobile ? "auto" : "visible",
                        "&::-webkit-scrollbar": { display: "none" },
                        scrollbarWidth: "none",
                    }}
                >
                    {/* {data?.map((cat) => {
                        const isActive = activeFilter === cat;
                        return (
                            <Chip
                                key={cat}
                                label={cat}
                                onClick={() => handleFilterClick(cat)}
                                className={`dashboard-chip ${isActive ? "chip-active" : "chip-inactive"
                                    }`}
                                sx={{ border: "1px solid #1672B9" }}
                            />
                        );
                    })} */}
                    {filterChips?.map((chip , index) => {

                        const label = chip?.name || chip;
                        const key = chip?.key || chip;

                        const isActive = activeFilter === label;

                        return (
                            <Chip
                            key={index}
                            label={label}
                            onClick={() => handleFilterClick(label)}
                            className={`dashboard-chip ${isActive ? "chip-active" : "chip-inactive"}`}
                            sx={{ border: "1px solid #1672B9" }}
                            />
                        );
                        })}

                </Box>}

            </Box>

            {/* Blue underline */}
            <Box
                className={
                    title === "Retailers"
                        ? "gradient-line-Retailers"
                        : title === "Distributors"
                            ? "gradient-line-Distributors" : title === 'Commercial Insights' ? 'gradient-line-Commercial-Insights' : title === 'Intelligence' ? 'gradient-line-Intelligence'
                                : "gradient-line-dashboard"
                }
                sx={{
                    width: "calc(100% + 82px)",
                    ml: "-40px",
                    mr: "-40px",
                    height: "4px",
                    // position: "relative",
                    zIndex: 11,
                }}
            />

            {/* Filter Panel */}
            <FilterPanelContainer
                openFilters={openFilters}
                setOpenFilters={setOpenFilters}
                filterPresets={filterPresets}
                filterOptions={filterOptions}
                onFiltersChange={handleFiltersChange}
            />
        </Box>


    );
};

export default DashboardHeader;
 